/**
 * Downscale and re-encode images before upload to avoid 413 (body too large).
 * Returns the original file if compression fails or the file is not an image.
 */
export function compressImageForUpload(file, { maxEdge = 1280, quality = 0.82 } = {}) {
  if (!file?.type?.startsWith('image/') || file.type === 'image/svg+xml') {
    return Promise.resolve(file);
  }

  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    const finish = (out) => {
      URL.revokeObjectURL(url);
      resolve(out || file);
    };

    img.onload = () => {
      try {
        let { width, height } = img;
        const scale = Math.min(1, maxEdge / Math.max(width, height));
        width = Math.max(1, Math.round(width * scale));
        height = Math.max(1, Math.round(height * scale));

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          finish(file);
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob || blob.size >= file.size) {
              finish(file);
              return;
            }
            const base = file.name.replace(/\.[^.]+$/, '') || 'category';
            finish(new File([blob], `${base}.jpg`, { type: 'image/jpeg' }));
          },
          'image/jpeg',
          quality
        );
      } catch {
        finish(file);
      }
    };

    img.onerror = () => finish(file);
    img.src = url;
  });
}
